import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Paper,
  Typography,
  InputAdornment,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import CakeIcon from '@mui/icons-material/Cake';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import RestoreIcon from '@mui/icons-material/Restore';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Item Component
const SortableItem = ({ item, editingId, onEdit, onDelete, calculateAge, getNextBirthday, reorderMode }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: !reorderMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const age = calculateAge(item.birthDate, item.yearKnown);
  const daysUntil = getNextBirthday(item.birthDate);

  // Format display date (DD/MM or DD/MM/YYYY)
  const displayDate = item.yearKnown !== false
    ? item.text.split(': ')[1]
    : item.text.split(': ')[1]?.replace(/\/\d{4}$/, '') || item.text.split(': ')[1];

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      secondaryAction={
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton edge="end" onClick={() => onEdit(item)}>
            <EditIcon />
          </IconButton>
          <IconButton edge="end" onClick={() => onDelete(item.id)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      }
      sx={{
        border: '1px solid',
        borderColor: editingId === item.id ? 'primary.main' : 'divider',
        borderRadius: 1,
        mb: 1,
        backgroundColor: editingId === item.id ? 'action.hover' : 'background.paper',
        cursor: isDragging ? 'grabbing' : 'default',
      }}
    >
      {reorderMode && (
        <ListItemIcon
          {...attributes}
          {...listeners}
          sx={{
            cursor: 'grab',
            touchAction: 'none',
            minWidth: 36,
            '&:active': {
              cursor: 'grabbing'
            }
          }}
        >
          <DragIndicatorIcon sx={{ color: 'text.secondary' }} />
        </ListItemIcon>
      )}
      <ListItemText
        primary={item.name}
        secondary={
          <>
            {displayDate}{age !== null ? ` (Age: ${age})` : ''}
            {daysUntil === 0 ? ' - 🎉 Today!' : ` - in ${daysUntil} days`}
          </>
        }
      />
    </ListItem>
  );
};

const BirthdayList = ({ list, onUpdateList }) => {
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState(null);
  const [yearKnown, setYearKnown] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [reorderMode, setReorderMode] = useState(false);

  // Sensors for drag and drop (pointer, touch, keyboard)
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleSaveBirthday = async () => {
    if (!name.trim() || !birthDate) return;

    const formattedDate = yearKnown
      ? birthDate.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })
      : birthDate.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit'
        });

    if (editingId) {
      // Update existing birthday
      const updatedItems = list.items.map(item =>
        item.id === editingId
          ? {
              ...item,
              name: name.trim(),
              birthDate: birthDate.toISOString(),
              yearKnown: yearKnown,
              text: `${name.trim()}: ${formattedDate}`
            }
          : item
      );
      await onUpdateList(list.id, { items: updatedItems });
      setEditingId(null);
    } else {
      // Add new birthday
      const newItem = {
        id: Date.now().toString(),
        name: name.trim(),
        birthDate: birthDate.toISOString(),
        yearKnown: yearKnown,
        text: `${name.trim()}: ${formattedDate}`,
        createdAt: new Date().toISOString()
      };
      const updatedItems = [...(list.items || []), newItem];
      await onUpdateList(list.id, { items: updatedItems });
    }

    setName('');
    setBirthDate(null);
    setYearKnown(true);
  };

  const handleEditBirthday = (item) => {
    setEditingId(item.id);
    setName(item.name);
    setBirthDate(new Date(item.birthDate));
    setYearKnown(item.yearKnown !== false);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setName('');
    setBirthDate(null);
    setYearKnown(true);
  };

  const handleDeleteBirthday = async (itemId) => {
    const updatedItems = list.items.filter(item => item.id !== itemId);
    await onUpdateList(list.id, { items: updatedItems });
    if (editingId === itemId) {
      setEditingId(null);
      setName('');
      setBirthDate(null);
      setYearKnown(true);
    }
  };

  const calculateAge = (birthDateStr, yearKnown) => {
    if (yearKnown === false) return null;
    const birth = new Date(birthDateStr);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const getNextBirthday = (birthDateStr) => {
    const birth = new Date(birthDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const thisYear = today.getFullYear();

    let nextBirthday = new Date(thisYear, birth.getMonth(), birth.getDate());
    nextBirthday.setHours(0, 0, 0, 0);

    if (nextBirthday < today) {
      nextBirthday = new Date(thisYear + 1, birth.getMonth(), birth.getDate());
      nextBirthday.setHours(0, 0, 0, 0);
    }

    const daysUntil = Math.floor((nextBirthday - today) / (1000 * 60 * 60 * 24));
    return daysUntil;
  };

  // Handle drag end event
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const items = list.items || [];
      const oldIndex = items.findIndex(item => item.id === active.id);
      const newIndex = items.findIndex(item => item.id === over.id);

      const reorderedItems = arrayMove(items, oldIndex, newIndex);
      await onUpdateList(list.id, { items: reorderedItems });
    }
  };

  // Reset order - sort by next birthday date
  const handleResetOrder = async () => {
    const items = list.items || [];
    const sortedItems = [...items].sort((a, b) => {
      const daysA = getNextBirthday(a.birthDate);
      const daysB = getNextBirthday(b.birthDate);
      return daysA - daysB;
    });
    await onUpdateList(list.id, { items: sortedItems });
  };

  // Use items in their stored order (user can manually reorder)
  const items = list.items || [];

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
        <Box sx={{ mb: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
          <TextField
            placeholder="Person's name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && name.trim() && birthDate) {
                handleSaveBirthday();
              }
            }}
            variant="outlined"
            size="medium"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon />
                </InputAdornment>
              )
            }}
            sx={{ flexGrow: 1 }}
          />

          <DatePicker
            value={birthDate}
            onChange={setBirthDate}
            format={yearKnown ? "dd/MM/yyyy" : "dd/MM"}
            views={yearKnown ? ['year', 'month', 'day'] : ['month', 'day']}
            slotProps={{
              textField: {
                variant: 'outlined',
                size: 'medium',
                placeholder: 'Birth date',
                onKeyPress: (e) => {
                  if (e.key === 'Enter' && name.trim() && birthDate) {
                    handleSaveBirthday();
                  }
                },
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <CakeIcon />
                    </InputAdornment>
                  )
                }
              }
            }}
            sx={{ width: { xs: '100%', sm: 200 } }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={!yearKnown}
                onChange={(e) => setYearKnown(!e.target.checked)}
                size="small"
              />
            }
            label="Year unknown"
            sx={{
              mx: 0,
              '& .MuiFormControlLabel-label': {
                fontSize: '0.875rem',
                whiteSpace: 'nowrap'
              }
            }}
          />

          <Button
            variant="contained"
            onClick={handleSaveBirthday}
            disabled={!name.trim() || !birthDate}
            startIcon={editingId ? <SaveIcon /> : <AddIcon />}
          >
            {editingId ? 'Update' : 'Add'}
          </Button>
          {editingId && (
            <Button
              variant="outlined"
              onClick={handleCancelEdit}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          )}
        </Box>
      </LocalizationProvider>

      <Box sx={{ mb: 0.5, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button
          variant={reorderMode ? 'contained' : 'outlined'}
          size="small"
          startIcon={<SwapVertIcon />}
          onClick={() => setReorderMode(!reorderMode)}
        >
          {reorderMode ? 'Done' : 'Reorder'}
        </Button>
        {reorderMode && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<RestoreIcon />}
            onClick={handleResetOrder}
          >
            Reset Order
          </Button>
        )}
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map(item => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <List>
            {items.length > 0 ? (
              items.map((item) => (
                <SortableItem
                  key={item.id}
                  item={item}
                  editingId={editingId}
                  onEdit={handleEditBirthday}
                  onDelete={handleDeleteBirthday}
                  calculateAge={calculateAge}
                  getNextBirthday={getNextBirthday}
                  reorderMode={reorderMode}
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No birthdays yet. Add your first one!
              </Typography>
            )}
          </List>
        </SortableContext>
      </DndContext>
    </Paper>
  );
};

export default BirthdayList;
